import { VisuallyHidden } from '@reach/visually-hidden';
import { is } from 'ramda';
import React, { ChangeEventHandler, ReactNode, forwardRef, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import styled from 'styled-components';

import { getColorFromTheme } from '@utils/theme';

import theme from '@theme/FolxTheme';

import BasicText from '../BasicText';

export interface ISelectableButtonProps {
	optionValue: string;
	value: string;
	onChange?: ControllerRenderProps['onChange'];
	onBlur?: ChangeEventHandler<HTMLInputElement>;
	alignment?: 'start' | 'center' | 'end';
	children?: ReactNode;
	className?: string;
	disabled?: boolean;
	error?: string;
	fullWidth?: boolean;
	name?: string;
	optionLabel?: string; // Add if children of component are not simple strings
}

interface IRadioButtonProps {
	alignment: 'start' | 'center' | 'end';
	disabled: boolean;
	error: boolean;
	focused: boolean;
	fullWidth: boolean;
	selected: boolean;
}

export const SelectionButton = styled.button<IRadioButtonProps>`
	align-items: center;
	background: ${({ selected, disabled, theme: components }) =>
		disabled
			? getColorFromTheme(
					components.selectableButton.disabledColor,
					components.selectableButton.disabledTint
			  )
			: selected
			? getColorFromTheme(components.selectableButton.color, components.selectableButton.tint)
			: theme.colors.white};
	border: ${({ selected, disabled, error, theme: components }) =>
		selected
			? `3px solid ${getColorFromTheme(
					components.selectableButton.borderColor,
					components.selectableButton.borderTint
			  )}`
			: disabled
			? `0px transparent`
			: error
			? `3px solid ${theme.tints.darken40(theme.colors.red)}`
			: `0.5px solid ${theme.colors.black}`};
	outline: ${({ focused }) => (focused ? '5px auto Highlight' : 'none')};
	outline: ${({ focused }) => (focused ? '5px auto -webkit-focus-ring-color' : 'none')};
	display: flex;
	font-size: ${theme.fontSizes.body.sm};
	justify-content: ${({ alignment }) => alignment};
	padding: ${({ selected, error }) =>
		selected || error ? `calc(${theme.spacing.md} - 3px)` : `calc(${theme.spacing.md} - 1px)`};
	text-align: ${({ alignment }) => alignment};
	text-decoration: none;
	width: ${({ fullWidth }) => fullWidth && '100%'};
`;

type Ref = HTMLInputElement;

const SelectableButton = forwardRef<Ref, ISelectableButtonProps>(
	(
		{
			alignment = 'center',
			children,
			className,
			disabled = false,
			error,
			fullWidth = false,
			name,
			value,
			onChange = () => {},
			onBlur = () => {},
			optionLabel,
			optionValue,
		},
		ref
	) => {
		const [focused, setFocused] = useState(false);

		const input = (
			<VisuallyHidden>
				<input
					aria-label={is(String, children) ? children : optionLabel}
					id={optionValue}
					name={name}
					checked={value === optionValue}
					disabled={disabled}
					type="radio"
					value={optionValue}
					onFocus={() => setFocused(true)}
					onBlur={(e) => {
						setFocused(false);
						onBlur(e);
					}}
					onChange={onChange}
					ref={ref}
				/>
			</VisuallyHidden>
		);

		return (
			<>
				{input}
				<SelectionButton
					alignment={alignment}
					aria-checked={value === optionValue}
					className={className}
					data-cy={name + '-' + optionValue}
					disabled={disabled}
					error={!!error}
					focused={focused}
					fullWidth={fullWidth}
					name={name}
					role="radio"
					selected={value === optionValue}
					tabIndex={-1}
					type="button"
					onClick={() => onChange(optionValue)}>
					{is(String, children) ? (
						<>
							<BasicText>
								<label htmlFor={name}>{children}</label>
							</BasicText>
						</>
					) : (
						<>{children}</>
					)}
				</SelectionButton>
			</>
		);
	}
);

SelectableButton.displayName = 'SelectableButton';
export default styled(SelectableButton)``;

/** Example Usage **
	import { map } from 'ramda';

	const [value, setValue] = useState('');
	const name = 'NameOfGroup';
	const values = [
		{ key: 'yes', ...remainingProps },
		{ key: 'no', ...remainingProps },
	];
	...
	return (
		...
		<SelectableButtonGroup layout={ 'row' | 'column' | 'multi' }>
		OR
		<SelectableButtonGrid role="radiogroup">
			{map(
				(v) => (
					<SelectableButton
						name={name}
						optionValue={v.key}
						key={v.key}
						value={value}
						onChange={(v) => {
							if (!!v.target) {
								setValue(v.target.value);
							} else {
								setValue(v);
							}
						}}
					>
						{v.children}
					</SelectableButton>
				),
				values
			)}
		</SelectableButtonGroup> / </SelectableButtonGrid>
		...
	);


** Example React-Hook-Form Usage **
	const { control } = useForm({
		defaultValues: {
			...existingFormData,
		},
	});
	...
	return (
		...
		<Controller
			name={name}
			control={control}
			render={({ field }) => (
				<SelectableButtonGroup layout="row">
					<SelectableButton
						optionValue={option1}
						{...field}>
							{children}
					</SelectableButton>
					<SelectableButton
						optionValue={option2}
						{...field}>
							{children}
					</SelectableButton>
					<SelectableButton
						optionValue={option3}
						{...field}>
							{children}
					</SelectableButton>
				</SelectableButtonGroup>
			)}
		/>
		...
	);
*/
